import { gql } from '@apollo/client';

// User queries
export const GET_USERS = gql`
  query GetUsers {
    users {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
      createdAt
    }
  }
`;

export const GET_USER = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
    }
  }
`;

export const GET_USER_BY_EMAIL = gql`
  query GetUserByEmail($email: String!) {
    userByEmail(email: $email) {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
    }
  }
`;

export const GET_USERS_BY_ROLE = gql`
  query GetUsersByRole($role: String!) {
    usersByRole(role: $role) {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
    }
  }
`;

export const GET_USERS_BY_DEPARTMENT = gql`
  query GetUsersByDepartment($department: String!) {
    usersByDepartment(department: $department) {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
    }
  }
`;

export const SEARCH_USERS = gql`
  query SearchUsers($query: String!) {
    searchUsers(query: $query) {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
    }
  }
`;

export const GET_USER_STATS = gql`
  query GetUserStats {
    userStats {
      totalUsers
      activeUsers
      usersByRole
      usersByDepartment
      usersByDesignation
      usersBySpecialization
    }
  }
`;

// Module queries
export const GET_MODULES = gql`
  query GetModules {
    modules {
      _id
      id
      title
      description
      duration
      difficulty
      icon
      category
      tags
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
      content {
        sections {
          title
          content
          video {
            src
            title
            caption
          }
        }
      }
      quiz {
        question
        options
        correct
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_MODULE = gql`
  query GetModule($id: String!) {
    module(id: $id) {
      _id
      id
      title
      description
      duration
      difficulty
      icon
      category
      tags
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
      content {
        sections {
          title
          content
          video {
            src
            title
            caption
          }
        }
      }
      quiz {
        question
        options
        correct
      }
    }
  }
`;

export const GET_MODULES_BY_DIFFICULTY = gql`
  query GetModulesByDifficulty($difficulty: String!) {
    modulesByDifficulty(difficulty: $difficulty) {
      _id
      id
      title
      description
      duration
      difficulty
    }
  }
`;

export const SEARCH_MODULES = gql`
  query SearchModules($query: String!) {
    searchModules(query: $query) {
      _id
      id
      title
      description
      duration
      difficulty
    }
  }
`;

export const GET_MODULES_FOR_USER = gql`
  query GetModulesForUser($user: UserInput!) {
    modulesForUser(user: $user) {
      _id
      id
      title
      description
      duration
      difficulty
      icon
      category
      tags
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
      content {
        sections {
          title
          content
          video {
            src
            title
            caption
          }
        }
      }
      quiz {
        question
        options
        correct
      }
    }
  }
`;

export const GET_MODULE_STATS = gql`
  query GetModuleStats {
    moduleStats {
      totalModules
      modulesByDifficulty
      totalSections
      totalQuizQuestions
      averageSectionsPerModule
      averageQuizQuestionsPerModule
    }
  }
`;

// Notification queries
export const GET_NOTIFICATIONS = gql`
  query GetNotifications($userEmail: String!) {
    notifications(userEmail: $userEmail) {
      _id
      title
      message
      type
      read
      moduleId
      createdAt
    }
  }
`;

export const GET_UNREAD_COUNT = gql`
  query GetUnreadCount($userEmail: String!) {
    unreadCount(userEmail: $userEmail)
  }
`;

// Progress queries
export const GET_PROGRESS = gql`
  query GetProgress($userEmail: String!) {
    progress(userEmail: $userEmail)
  }
`;

export const GET_PROGRESS_BY_MODULE = gql`
  query GetProgressByModule($userEmail: String!, $moduleId: String!) {
    progressByModule(userEmail: $userEmail, moduleId: $moduleId) {
      _id
      completed
      completedAt
      quizCompleted
      quizScore
      quizCompletedAt
      timeSpent
      currentSection
      selectedAnswers
    }
  }
`;

export const GET_PROGRESS_STATS = gql`
  query GetProgressStats($userEmail: String!) {
    progressStats(userEmail: $userEmail) {
      completedModules
      totalModules
      averageScore
      totalTimeSpent
    }
  }
`;

// Auth queries
export const VERIFY_AUTH = gql`
  query VerifyAuth($email: String!) {
    verifyAuth(email: $email) {
      success
      user {
        _id
        email
        name
        role
        department
        designation
        specialization
      }
      error
    }
  }
`;

// Schedule queries
export const GET_SCHEDULES = gql`
  query GetSchedules {
    schedules {
      _id
      id
      moduleId
      title
      description
      startDate
      endDate
      startTime
      endTime
      location
      instructor
      maxParticipants
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_SCHEDULE = gql`
  query GetSchedule($id: String!) {
    schedule(id: $id) {
      _id
      id
      moduleId
      title
      description
      startDate
      endDate
      startTime
      endTime
      location
      instructor
      maxParticipants
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
    }
  }
`;

export const GET_SCHEDULES_BY_MODULE = gql`
  query GetSchedulesByModule($moduleId: String!) {
    schedulesByModule(moduleId: $moduleId) {
      _id
      id
      moduleId
      title
      description
      startDate
      endDate
      startTime
      endTime
      location
      instructor
      maxParticipants
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
    }
  }
`;

// Prescreening queries
export const GET_PRESCREENING = gql`
  query GetPrescreening($userEmail: String!) {
    prescreening(userEmail: $userEmail) {
      _id
      userEmail
      category
      level
      profession
      goal
      tags
      completed
      skipped
      createdAt
      updatedAt
    }
  }
`;

// Settings queries
export const GET_SETTINGS = gql`
  query GetSettings {
    settings {
      _id
      key
      value
      description
      category
      createdAt
      updatedAt
    }
  }
`;

export const GET_SETTING = gql`
  query GetSetting($key: String!) {
    setting(key: $key) {
      _id
      key
      value
      description
      category
    }
  }
`;

export const GET_SETTINGS_BY_CATEGORY = gql`
  query GetSettingsByCategory($category: String!) {
    settingsByCategory(category: $category) {
      _id
      key
      value
      description
      category
    }
  }
`;

// Tag queries
export const GET_TAGS = gql`
  query GetTags {
    tags {
      _id
      name
      displayName
      description
      category
      isActive
      order
      createdAt
      updatedAt
    }
  }
`;

export const GET_TAG = gql`
  query GetTag($name: String!) {
    tag(name: $name) {
      _id
      name
      displayName
      description
      category
      isActive
      order
      createdAt
      updatedAt
    }
  }
`;

export const GET_TAGS_BY_CATEGORY = gql`
  query GetTagsByCategory($category: String!) {
    tagsByCategory(category: $category) {
      _id
      name
      displayName
      description
      category
      isActive
      order
      createdAt
      updatedAt
    }
  }
`;

export const GET_ACTIVE_TAGS = gql`
  query GetActiveTags {
    activeTags {
      _id
      name
      displayName
      description
      category
      isActive
      order
      createdAt
      updatedAt
    }
  }
`;

// Role queries
export const GET_ROLES = gql`
  query GetRoles {
    roles {
      _id
      name
      displayName
      description
      isActive
      order
      permissions
      createdAt
      updatedAt
    }
  }
`;

export const GET_ROLE = gql`
  query GetRole($name: String!) {
    role(name: $name) {
      _id
      name
      displayName
      description
      isActive
      order
      permissions
      createdAt
      updatedAt
    }
  }
`;

export const GET_ACTIVE_ROLES = gql`
  query GetActiveRoles {
    activeRoles {
      _id
      name
      displayName
      description
      isActive
      order
      permissions
      createdAt
      updatedAt
    }
  }
`;

